// API Configuration for Firebase Functions deployment
const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://us-central1-fenparet-website.cloudfunctions.net/api'
    : 'http://localhost:5001/fenparet-website/us-central1/api');

export const apiConfig = {
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  credentials: 'include', // CRITICAL: Include httpOnly cookies for all requests
  mode: 'cors', // Explicitly set CORS mode
};

// Complete API endpoints
export const apiEndpoints = {
  // Authentication
  auth: {
    login: `${API_BASE_URL}/auth/login`,
    register: `${API_BASE_URL}/auth/register`,
    logout: `${API_BASE_URL}/auth/logout`,
    me: `${API_BASE_URL}/auth/me`,
    refresh: `${API_BASE_URL}/auth/refresh`,
  },
  
  // Products
  products: {
    list: `${API_BASE_URL}/products`,
    byId: (id) => `${API_BASE_URL}/products/${id}`,
    featured: `${API_BASE_URL}/products/featured`,
    newArrivals: `${API_BASE_URL}/products/new-arrivals`,
    search: `${API_BASE_URL}/products/search`,
    byCategory: (slug) => `${API_BASE_URL}/products/category/${slug}`,
    categories: `${API_BASE_URL}/products/categories`,
  },
  
  // Orders
  orders: {
    create: `${API_BASE_URL}/orders`,
    byId: (id) => `${API_BASE_URL}/orders/${id}`,
    track: (orderNumber) => `${API_BASE_URL}/orders/track/${orderNumber}`,
    userOrders: (userId) => `${API_BASE_URL}/orders/user/${userId}`,
  },
  
  // Reviews
  reviews: {
    create: `${API_BASE_URL}/reviews`,
    byProduct: (productId) => `${API_BASE_URL}/reviews/product/${productId}`,
    vote: (reviewId) => `${API_BASE_URL}/reviews/${reviewId}/vote`,
    canReview: (productId) => `${API_BASE_URL}/reviews/can-review/${productId}`,
    myReviews: `${API_BASE_URL}/reviews/my-reviews`,
  },
  
  // Support Tickets
  tickets: {
    create: `${API_BASE_URL}/tickets`,
    byId: (id) => `${API_BASE_URL}/tickets/${id}`,
    userTickets: (userId) => `${API_BASE_URL}/tickets/user/${userId}`,
  },
  
  // Admin
  admin: {
    dashboard: `${API_BASE_URL}/admin/dashboard`,
    users: `${API_BASE_URL}/admin/users`,
    products: `${API_BASE_URL}/admin/products`,
    orders: `${API_BASE_URL}/admin/orders`,
    tickets: `${API_BASE_URL}/admin/tickets`,
    analytics: `${API_BASE_URL}/admin/analytics`,
    inventory: {
      alerts: `${API_BASE_URL}/admin/inventory/alerts`,
      bulkUpdate: `${API_BASE_URL}/admin/inventory/bulk-update`,
    },
    categories: `${API_BASE_URL}/admin/categories`,
    settings: `${API_BASE_URL}/admin/settings`,
  },
  
  // Feedback and ratings
  feedback: {
    submit: `${API_BASE_URL}/feedback/submit`,
    stats: `${API_BASE_URL}/feedback/stats`,
  },
};

// Helper function to make API requests
export const apiRequest = async (url, options = {}) => {
  // Enhanced authentication: cookies + token backup
  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  
  const headers = {
    ...apiConfig.headers,
    ...options.headers,
  };
  
  // When sending FormData, let the browser set the Content-Type
  if (options.body instanceof FormData) {
    delete headers['Content-Type'];
  }
  
  // Add token as backup authentication if available
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const config = {
    ...apiConfig,
    ...options,
    headers,
  };
  
  try {
    const response = await fetch(url, config);
    
    // Handle non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return { data: await response.text(), status: response.status };
    }
    
    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(data.message || `HTTP error! status: ${response.status}`);
    }
    
    return { data, status: response.status };
  } catch (error) {
    console.error('API request failed:', error);
    throw error;
  }
};

// Specific API functions
export const api = {
  // Authentication
  login: (credentials) => 
    apiRequest(apiEndpoints.auth.login, {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),
    
  register: (userData) =>
    apiRequest(apiEndpoints.auth.register, {
      method: 'POST',
      body: JSON.stringify(userData),
    }),
    
  getCurrentUser: () =>
    apiRequest(apiEndpoints.auth.me),
    
  // Products
  getProducts: (params = {}) => {
    const url = new URL(apiEndpoints.products.list);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });
    return apiRequest(url.toString());
  },
  
  getProduct: (id) =>
    apiRequest(apiEndpoints.products.byId(id)),
    
  getFeaturedProducts: () =>
    apiRequest(apiEndpoints.products.featured),
    
  // Orders
  createOrder: (orderData) =>
    apiRequest(apiEndpoints.orders.create, {
      method: 'POST',
      body: JSON.stringify(orderData),
    }),
    
  trackOrder: (orderNumber) =>
    apiRequest(apiEndpoints.orders.track(orderNumber)),
    
  // Reviews
  createReview: (reviewData) =>
    apiRequest(apiEndpoints.reviews.create, {
      method: 'POST',
      body: JSON.stringify(reviewData),
    }),
    
  getProductReviews: (productId) =>
    apiRequest(apiEndpoints.reviews.byProduct(productId)),
    
  // Admin
  getDashboardStats: () =>
    apiRequest(apiEndpoints.admin.dashboard),
    
  getAdminProducts: (params = {}) => {
    const url = new URL(apiEndpoints.admin.products);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, params[key]);
      }
    });
    return apiRequest(url.toString());
  },
  
  createProduct: (productData) =>
    apiRequest(apiEndpoints.admin.products, {
      method: 'POST',
      body: JSON.stringify(productData),
    }),

  updateProduct: (id, productData) =>
    apiRequest(`${apiEndpoints.admin.products}/${id}`, {
      method: 'PUT',
      body: JSON.stringify(productData),
    }),
    
  deleteProduct: (id) =>
    apiRequest(`${API_BASE_URL}/admin/products/${id}`, {
      method: 'DELETE',
    }),
    
  // Feedback (creates support tickets)
  submitFeedback: (feedbackData) => {
    // Transform feedback data into ticket format
    const ticketData = {
      subject: `Feedback: ${feedbackData.type.charAt(0).toUpperCase() + feedbackData.type.slice(1)}`,
      description: feedbackData.message,
      category: feedbackData.type === 'bug' ? 'technical' : 
                feedbackData.type === 'payment' ? 'billing' : 
                feedbackData.type === 'feature' ? 'other' : 'technical',
      priority: feedbackData.rating <= 2 ? 'high' : 
                feedbackData.rating <= 3 ? 'medium' : 'low',
      customerName: 'Utilisateur Anonyme',
      customerEmail: feedbackData.email || 'noreply@fenkparet.com',
      source: 'feedback_widget',
      metadata: {
        rating: feedbackData.rating,
        userAgent: feedbackData.userAgent,
        page: feedbackData.page,
        url: feedbackData.url,
        screenSize: feedbackData.screenSize,
        viewportSize: feedbackData.viewportSize,
        timestamp: feedbackData.timestamp
      }
    };
    
    return apiRequest(`${process.env.NEXT_PUBLIC_API_URL}/tickets`, {
      method: 'POST',
      body: JSON.stringify(ticketData),
    });
  },
};

export default api;